import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Edit2, X, User, Ruler, Phone, Mail, MapPin, AlertCircle, Save, Loader2, Calendar, UserCircle, Send, Instagram, Facebook, Eye } from 'lucide-react';
import { toast } from 'sonner';

// Import refactored sub-components
import StaffManagementSection from './profile/StaffManagementSection';
import ManagerManagementSection from './profile/ManagerManagementSection';

import { API } from '../config/api';

const SPORTS = ['Football', 'Rugby', 'Tennis', 'Swimming', 'Athletics', 'Basketball', 'Cricket', 'Hockey', 'Gymnastics', 'Multi-sport'];
const GENDERS = ['male', 'female', 'other'];

export default function InlineProfileEditor({ child, onUpdate, isViewOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [teamManagers, setTeamManagers] = useState([]);
  const [hasQuestionnaire, setHasQuestionnaire] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: child?.first_name || '',
    last_name: child?.last_name || '',
    date_of_birth: child?.date_of_birth || '',
    gender: child?.gender || '',
    maternal_height: child?.maternal_height || '',
    paternal_height: child?.paternal_height || '',
    primary_sport: child?.primary_sport || '',
    mobile_phone: child?.mobile_phone || '',
    player_email: child?.player_email || child?.email || '',
    parent_email: child?.parent_email || '',
    address: child?.address || '',
    emergency_contact: child?.emergency_contact || '',
    assigned_coach_name: child?.assigned_coach_name || '',
    assigned_team_manager_id: child?.assigned_team_manager_id || '',
    assigned_team_manager_name: child?.assigned_team_manager_name || '',
    // Social Media
    instagram_handle: child?.instagram_handle || '',
    facebook_handle: child?.facebook_handle || '',
    tiktok_handle: child?.tiktok_handle || ''
  });

  // Load available coaches and team managers
  const loadStaff = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [staffRes, managersRes, questionnaireRes] = await Promise.all([
        axios.get(`${API}/auth/epp-staff`, { headers }).catch(() => ({ data: { epp_staff: [] } })),
        axios.get(`${API}/auth/team-managers`, { headers }).catch(() => ({ data: { team_managers: [] } })),
        child?.id ? axios.get(`${API}/medical/questionnaire/${child.id}`, { headers }).catch(() => ({ data: { questionnaire: null } })) : Promise.resolve({ data: { questionnaire: null } })
      ]);
      
      setCoaches(staffRes.data.epp_staff || []);
      
      // Ensure the currently assigned manager is in the list
      let managers = managersRes.data.team_managers || [];
      if (child?.assigned_team_manager_id && child?.assigned_team_manager_name) {
        const currentManagerInList = managers.some(m => m.id === child.assigned_team_manager_id);
        if (!currentManagerInList) {
          managers = [...managers, {
            id: child.assigned_team_manager_id,
            full_name: child.assigned_team_manager_name,
            email: 'N/A'
          }];
        }
      }
      setTeamManagers(managers);
      setHasQuestionnaire(!!questionnaireRes.data.questionnaire);
    } catch (error) {
      console.error('Failed to load staff:', error);
    }
  }, [child?.id, child?.assigned_team_manager_id, child?.assigned_team_manager_name]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    if (child) {
      setFormData({
        first_name: child.first_name || '',
        last_name: child.last_name || '',
        date_of_birth: child.date_of_birth ? child.date_of_birth.split('T')[0] : '',
        gender: child.gender || '',
        maternal_height: child.maternal_height || '',
        paternal_height: child.paternal_height || '',
        primary_sport: child.primary_sport || '',
        mobile_phone: child.mobile_phone || '',
        email: child.email || '',
        player_email: child.player_email || '',
        parent_email: child.parent_email || '',
        address: child.address || '',
        emergency_contact: child.emergency_contact || '',
        assigned_coach_name: child.assigned_coach_name || '',
        assigned_team_manager_id: child.assigned_team_manager_id || '',
        assigned_team_manager_name: child.assigned_team_manager_name || '',
        instagram_handle: child.instagram_handle || '',
        facebook_handle: child.facebook_handle || '',
        tiktok_handle: child.tiktok_handle || ''
      });
    }
  }, [child]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        ...formData,
        maternal_height: formData.maternal_height ? parseFloat(formData.maternal_height) : null,
        paternal_height: formData.paternal_height ? parseFloat(formData.paternal_height) : null
      };
      
      await axios.put(`${API}/children/${child.id}`, updateData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Profile updated!');
      setEditing(false);
      if (onUpdate) onUpdate(updateData);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sendParentInvitation = async () => {
    if (!formData.parent_email) {
      toast.error('Please add a parent email address first');
      return;
    }
    
    setSendingInvite(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/${child.id}/send-invitation`, {
        parent_email: formData.parent_email
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Parent invitation sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const [sendingPlayerInvite, setSendingPlayerInvite] = useState(false);
  const sendPlayerInvitation = async () => {
    if (!formData.player_email) {
      toast.error('Please add a player email address first');
      return;
    }
    
    setSendingPlayerInvite(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/${child.id}/send-player-invitation`, {
        player_email: formData.player_email,
        player_name: `${formData.first_name} ${formData.last_name}`
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Player invitation sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send player invitation');
    } finally {
      setSendingPlayerInvite(false);
    }
  };

  const [sendingManagerInvite, setSendingManagerInvite] = useState(false);
  const sendManagerInvitation = async () => {
    if (!formData.assigned_team_manager_id) {
      toast.error('Please assign a team manager first');
      return;
    }
    
    // Find the manager's email
    const manager = teamManagers.find(m => m.id === formData.assigned_team_manager_id);
    if (!manager?.email) {
      toast.error('Team manager email not available');
      return;
    }
    
    setSendingManagerInvite(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/resend-team-manager-invite`, {
        manager_id: formData.assigned_team_manager_id
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Team manager invitation resent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send manager invitation');
    } finally {
      setSendingManagerInvite(false);
    }
  };

  // Handler for coach change from sub-component
  const handleCoachChange = (coachName) => {
    setFormData(prev => ({ ...prev, assigned_coach_name: coachName }));
    if (onUpdate) onUpdate({ assigned_coach_name: coachName });
  };

  // Handler for manager change from sub-component
  const handleManagerChange = ({ id, name }) => {
    setFormData(prev => ({ 
      ...prev, 
      assigned_team_manager_id: id || '',
      assigned_team_manager_name: name || ''
    }));
    if (onUpdate) onUpdate({ 
      assigned_team_manager_id: id || null,
      assigned_team_manager_name: name || null
    });
  };

  // Check if key details are missing
  const isDefaultDOB = formData.date_of_birth === '2010-01-01';
  const isIncomplete = !child?.maternal_height || !child?.paternal_height || !child?.mobile_phone || !child?.emergency_contact;
  const needsBasicInfo = isDefaultDOB || !formData.gender || !formData.parent_email;

  if (!child) return null;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="profile-editor">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <User className="h-5 w-5 text-[#02b5ff]" />
          <div>
            <h3 className="font-bold text-white flex items-center gap-2">
              Profile Details
              {isViewOnly && (
                <span className="text-xs text-zinc-500 flex items-center gap-1 font-normal">
                  <Eye className="h-3 w-3" /> View Only
                </span>
              )}
            </h3>
            {!isViewOnly && needsBasicInfo && !editing && (
              <p className="text-xs text-yellow-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Basic info needs updating (DOB, gender, or email)
              </p>
            )}
            {!isViewOnly && !needsBasicInfo && isIncomplete && !editing && (
              <p className="text-xs text-yellow-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Complete your profile for better predictions
              </p>
            )}
          </div>
        </div>
        {!isViewOnly && !editing ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="border-zinc-700 text-zinc-400 hover:text-white"
            data-testid="edit-profile-btn"
          >
            <Edit2 className="h-3 w-3 mr-1" /> Edit
          </Button>
        ) : !isViewOnly && editing ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#02b5ff] text-white"
              data-testid="save-profile-btn"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(false)}
              className="border-zinc-700"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : null}
      </div>

      <div className="p-4 space-y-4">
        {/* Basic Info - Name, DOB, Gender */}
        <div className="bg-zinc-800/30 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <UserCircle className="h-3 w-3" />
            Basic Information
            {needsBasicInfo && (
              <span className="text-yellow-500 text-[10px] font-normal normal-case ml-2">⚠️ Update required</span>
            )}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">First Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="first-name-input"
                />
              ) : (
                <p className="text-sm text-white">{formData.first_name}</p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Last Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="last-name-input"
                />
              ) : (
                <p className="text-sm text-white">{formData.last_name}</p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Date of Birth
                {isDefaultDOB && <span className="text-yellow-500 text-[10px]">⚠️</span>}
              </label>
              {editing ? (
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="dob-input"
                />
              ) : (
                <p className={`text-sm ${isDefaultDOB ? 'text-yellow-500' : 'text-white'}`}>
                  {formData.date_of_birth ? new Date(formData.date_of_birth).toLocaleDateString() : 'Not set'}
                  {isDefaultDOB && ' (default)'}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Gender</label>
              {editing ? (
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="gender-select"
                >
                  <option value="">Select gender</option>
                  {GENDERS.map(g => (
                    <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                  ))}
                </select>
              ) : (
                <p className={`text-sm ${formData.gender ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.gender ? formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1) : 'Not set'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* EPP Staff Assignment - Using refactored component */}
        <div className="bg-zinc-800/30 rounded-lg p-3">
          <StaffManagementSection
            coaches={coaches}
            currentCoach={formData.assigned_coach_name}
            childId={child?.id}
            isViewOnly={isViewOnly}
            onCoachChange={handleCoachChange}
            onCoachesRefresh={loadStaff}
          />
        </div>

        {/* Team Manager Assignment - Using refactored component */}
        {!isViewOnly && (
          <div className="bg-zinc-800/30 rounded-lg p-3">
            <ManagerManagementSection
              managers={teamManagers}
              currentManagerId={formData.assigned_team_manager_id}
              currentManagerName={formData.assigned_team_manager_name}
              childId={child?.id}
              isViewOnly={isViewOnly}
              onManagerChange={handleManagerChange}
              onManagersRefresh={loadStaff}
            />
            <p className="text-xs text-zinc-500 mt-2">
              Team Managers can only view this athlete's data. They cannot edit profiles or add records.
            </p>
          </div>
        )}

        {/* Parental Heights */}
        <div className="bg-zinc-800/30 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Ruler className="h-3 w-3" />
            Parental Heights (for predictions)
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Mother's Height (cm)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.1"
                  value={formData.maternal_height}
                  onChange={(e) => setFormData({ ...formData, maternal_height: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="e.g., 165.0"
                />
              ) : (
                <p className={`text-sm ${formData.maternal_height ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.maternal_height ? `${formData.maternal_height} cm` : 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Father's Height (cm)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.1"
                  value={formData.paternal_height}
                  onChange={(e) => setFormData({ ...formData, paternal_height: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="e.g., 180.0"
                />
              ) : (
                <p className={`text-sm ${formData.paternal_height ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.paternal_height ? `${formData.paternal_height} cm` : 'Not set'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sport */}
        <div className="bg-zinc-800/30 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
            Primary Sport
          </h4>
          {editing ? (
            <select
              value={formData.primary_sport}
              onChange={(e) => setFormData({ ...formData, primary_sport: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">Select sport</option>
              {SPORTS.map(sport => (
                <option key={sport} value={sport}>{sport}</option>
              ))}
            </select>
          ) : (
            <p className={`text-sm ${formData.primary_sport ? 'text-white' : 'text-zinc-600 italic'}`}>
              {formData.primary_sport || 'Not set'}
            </p>
          )}
        </div>

        {/* Contact Details */}
        <div className="bg-zinc-800/30 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Phone className="h-3 w-3" />
            Contact Details
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <Phone className="h-3 w-3" /> Mobile Phone
              </label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.mobile_phone}
                  onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="e.g., 07123 456789"
                />
              ) : (
                <p className={`text-sm ${formData.mobile_phone ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.mobile_phone || 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <Mail className="h-3 w-3" /> Player Email
              </label>
              {editing ? (
                <input
                  type="email"
                  value={formData.player_email}
                  onChange={(e) => setFormData({ ...formData, player_email: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="e.g., player@email.com"
                  data-testid="player-email-input"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <p className={`text-sm flex-1 ${formData.player_email ? 'text-white' : 'text-zinc-600 italic'}`}>
                    {formData.player_email || 'Not set'}
                  </p>
                  {!isViewOnly && (
                    formData.player_email ? (
                      <Button
                        size="sm"
                        onClick={sendPlayerInvitation}
                        disabled={sendingPlayerInvite}
                        className="h-7 px-2 bg-[#02b5ff] hover:bg-[#0090cc] text-white text-xs"
                        data-testid="send-player-invite-btn"
                      >
                        {sendingPlayerInvite ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-3 w-3 mr-1" />
                            Invite Player
                          </>
                        )}
                      </Button>
                    ) : (
                      <span className="text-xs text-zinc-500 italic">Add email to invite</span>
                    )
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <Mail className="h-3 w-3" /> Parent Email
                {!formData.parent_email && <span className="text-yellow-500 text-[10px]">⚠️ Required for invitation</span>}
              </label>
              {editing ? (
                <input
                  type="email"
                  value={formData.parent_email}
                  onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="e.g., parent@email.com"
                  data-testid="parent-email-input"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <p className={`text-sm flex-1 ${formData.parent_email ? 'text-white' : 'text-zinc-600 italic'}`}>
                    {formData.parent_email || 'Not set'}
                  </p>
                  {!isViewOnly && (
                    formData.parent_email ? (
                      <Button
                        size="sm"
                        onClick={sendParentInvitation}
                        disabled={sendingInvite}
                        className="h-7 px-2 bg-[#ff025f] hover:bg-[#dd0050] text-white text-xs"
                        data-testid="send-parent-invite-btn"
                      >
                        {sendingInvite ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-3 w-3 mr-1" />
                            Invite Parent
                          </>
                        )}
                      </Button>
                    ) : (
                      <span className="text-xs text-zinc-500 italic">Add email to invite</span>
                    )
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Address
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="e.g., 123 Main Street, London"
                />
              ) : (
                <p className={`text-sm ${formData.address ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.address || 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Emergency Contact
                {hasQuestionnaire && formData.emergency_contact && (
                  <span className="text-green-500 text-[10px] ml-1">✓ Synced from questionnaire</span>
                )}
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="e.g., John Doe - 07987 654321"
                />
              ) : (
                <p className={`text-sm ${formData.emergency_contact ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.emergency_contact || 'Not set'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-zinc-800/30 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Instagram className="h-3 w-3" />
            Social Media
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <Instagram className="h-3 w-3" /> Instagram
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.instagram_handle}
                  onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="@username"
                />
              ) : (
                <p className={`text-sm ${formData.instagram_handle ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.instagram_handle || 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <Facebook className="h-3 w-3" /> Facebook
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.facebook_handle}
                  onChange={(e) => setFormData({ ...formData, facebook_handle: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="username or profile URL"
                />
              ) : (
                <p className={`text-sm ${formData.facebook_handle ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.facebook_handle || 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1">
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg> TikTok
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.tiktok_handle}
                  onChange={(e) => setFormData({ ...formData, tiktok_handle: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="@username"
                />
              ) : (
                <p className={`text-sm ${formData.tiktok_handle ? 'text-white' : 'text-zinc-600 italic'}`}>
                  {formData.tiktok_handle || 'Not set'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
